/**
 * Invoice API client — functions for all invoice endpoints.
 *
 * Requirements: 44.12, 44.13, 44.14, 44.15, 44.16, 44.19, 44.32, 44.33
 */

import { apiClient } from '@/services/apiClient';
import type { InvoiceRecord, InvoiceLineItem, PaginatedResponse } from '@/types';

export interface InvoiceListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  vendorId?: number;
  lifecycleStatus?: string;
  departmentScope?: string;
  siteNumber?: string;
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface InvoiceDetailResponse {
  invoice: InvoiceRecord;
  lineItems: InvoiceLineItem[];
}

export interface CodeLineItemInput {
  lineItemId: number;
  glCode?: string | null;
  costCenter?: string | null;
  budgetCategory?: string | null;
  coderInitials?: string | null;
  siteNumber?: string | null;
}

export interface CodeInvoiceInput {
  lineItems: CodeLineItemInput[];
}

export interface CodeInvoiceResponse {
  invoiceId: number;
  lineItems: InvoiceLineItem[];
}

export const invoiceService = {
  /** List invoices with search, sort, filter, pagination. */
  list(params?: InvoiceListParams): Promise<PaginatedResponse<InvoiceRecord>> {
    return apiClient.get('invoices', params as Record<string, string | number | boolean | undefined>);
  },

  /** Get invoice detail with line items. */
  getById(id: number): Promise<InvoiceDetailResponse> {
    return apiClient.get(`invoices/${id}`);
  },

  /** Get coding queue — Pending Review invoices scoped to user's department. */
  getCodingQueue(): Promise<InvoiceRecord[]> {
    return apiClient.get('invoices/coding-queue');
  },

  /** Get approval queue — Pending VP Approval invoices scoped to user's department. */
  getApprovalQueue(): Promise<InvoiceRecord[]> {
    return apiClient.get('invoices/approval-queue');
  },

  /** Update GL codes, categories, associations on line items. */
  codeInvoice(invoiceId: number, input: CodeInvoiceInput): Promise<CodeInvoiceResponse> {
    return apiClient.put(`invoices/${invoiceId}/code`, input);
  },

  /** Submit invoice for VP review (transition to Pending VP Approval). */
  submitForReview(invoiceId: number): Promise<InvoiceRecord> {
    return apiClient.post(`invoices/${invoiceId}/submit-for-review`);
  },

  /** Approve invoice (VP). */
  approve(invoiceId: number): Promise<InvoiceRecord> {
    return apiClient.post(`invoices/${invoiceId}/approve`);
  },

  /** Reject invoice with notes (VP). */
  reject(invoiceId: number, notes: string): Promise<InvoiceRecord> {
    return apiClient.post(`invoices/${invoiceId}/reject`, { notes });
  },
};
